// ============================================================
// FILE: MyBookings.js
// LOCATION: C:\gardenmarket\web\src\pages\gardener\MyBookings.js
// ============================================================
// PURPOSE:
//   Gardener's "My Bookings" page. Rebuilt as an email-client
//   style master-detail layout, mirroring BuyerDashboard.js —
//   compact list (date / job title + buyer name / status) on the
//   left, full booking detail pane on the right.
//
// ROUTE: /gardener/bookings
//
// CALLS API:
//   GET /api/jobs/bookings
//
// LAYOUT:
//   MasterDetailLayout.js + BookingRow.js + BookingDetailContent.js
//   (the same component the standalone /gardener/booking/:id page
//   uses).
// ============================================================

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import client from '../../api/client';
import MasterDetailLayout from '../../components/MasterDetailLayout';
import BookingRow from './BookingRow';
import BookingDetailContent from './BookingDetailContent';
import { colors, radius, space } from '../../theme/theme';

export default function MyBookings() {
    const [bookings, setBookings] = useState([]);
    const [loading,  setLoading]  = useState(true);
    const [filter,   setFilter]   = useState('');
    const [selectedJobId, setSelectedJobId] = useState(null);

    const loadBookings = () => {
        return client.get('/jobs/bookings', { params: { status_code: filter || undefined } })
            .then(r => setBookings(r.data.bookings));
    };

    useEffect(() => {
        setLoading(true);
        loadBookings().catch(console.error).finally(() => setLoading(false));
    }, [filter]);

    const handleJobChanged = () => {
        loadBookings().catch(console.error);
    };

    const items = bookings.map(b => ({ ...b, id: b.job_id }));

    const listHeader = (
        <div style={styles.headerBlock}>
            <h1 style={styles.title}>My Bookings</h1>
            <p style={styles.sub}>Jobs where your quote was accepted</p>
            <div style={styles.filters}>
                {[
                    { v:'',            l:'All' },
                    { v:'accepted',    l:'Upcoming' },
                    { v:'in_progress', l:'In progress' },
                    { v:'completed',   l:'Completed' },
                    { v:'reviewed',    l:'Reviewed' },
                ].map(f => (
                    <button key={f.v} style={filter===f.v ? styles.filterOn : styles.filterOff} onClick={()=>setFilter(f.v)}>
                        {f.l}
                    </button>
                ))}
            </div>
        </div>
    );

    if (loading) return <div style={styles.loading}>Loading your bookings...</div>;

    return (
        <div style={styles.page}>
            {bookings.length === 0 ? (
                <>
                    {listHeader}
                    <div style={styles.empty}>
                        <p>No bookings yet.</p>
                        <p style={{fontSize:'0.85rem', color:'#888', marginTop:'6px'}}>
                            Once a buyer accepts one of your quotes, it'll show up here.
                        </p>
                        <Link to="/gardener/dashboard" style={styles.btnBrowse}>Browse Open Jobs</Link>
                    </div>
                </>
            ) : (
                <MasterDetailLayout
                    items={items}
                    selectedId={selectedJobId}
                    onSelect={setSelectedJobId}
                    listHeader={listHeader}
                    columns={['Date', 'Job', 'Status']}
                    renderRow={(job, isSelected, onClick) => <BookingRow key={job.id} job={job} isSelected={isSelected} onClick={onClick} />}
                    renderDetail={() => (
                        <BookingDetailContent
                            jobId={selectedJobId}
                            embedded={true}
                            onJobChanged={handleJobChanged}
                        />
                    )}
                    emptyDetailText="Select a booking from the list to view its details."
                />
            )}
        </div>
    );
}

const styles = {
    page:        { maxWidth:'1300px', margin:'0 auto', padding: space.xl + ' 24px' },
    loading:     { textAlign:'center', padding:'60px', color: colors.textMuted },
    empty:       { textAlign:'center', padding:'60px', color: colors.textMuted },
    headerBlock: { padding:'16px 18px 4px' },
    title:       { color: colors.text, marginBottom:'2px', fontSize:'1.2rem', fontWeight:700 },
    sub:         { color: colors.textFaint, marginBottom:'16px', fontSize:'0.8rem' },
    filters:     { display:'flex', gap:'6px', marginBottom:'16px', flexWrap:'wrap' },
    filterOff:   { padding:'5px 12px', borderRadius: radius.pill, border:`1px solid ${colors.border}`, background:'white', cursor:'pointer', fontSize:'0.74rem', color: colors.textMuted },
    filterOn:    { padding:'5px 12px', borderRadius: radius.pill, border:`1px solid ${colors.brand}`, background: colors.brand, color:'white', cursor:'pointer', fontSize:'0.74rem', fontWeight:600 },
    btnBrowse:   { display:'inline-block', marginTop:'16px', backgroundColor: colors.brand, color:'white', padding:'10px 24px', borderRadius: radius.sm, textDecoration:'none', fontWeight:600 },
};
